import type { TestUserRole } from "./api";

export const NAV_IDS = {
  dashboard: [
    "overview",
    "grades",
    "eLearning",
    "btec",
    "assessment",
    "parentAssessments",
    "parentInternship",
    "parentSubstitute",
    "substitute",
    "administration",
    "studentCare",
    "chat",
    "wellbeing",
    "messages",
    "foodOrders",
    "foodPhotos",
    "foodStudents",
    "foodStaff",
    "feedback",
    "help",
    "users",
    "settings",
  ],
  btec: [
    "dashboard",
    "studentStatus",
    "studentCalendar",
    "parentCalendar",
    "parentFeedback",
    "parentWorksheets",
    "projects",
    "personalTasks",
    "myTasks",
    "assessment",
    "standardisation",
    "reports",
    "guides",
    "notifications",
    "analytics",
    "scenarioRequests",
    "settings",
    "units",
    "templates",
  ],
  btecProject: [
    "overview",
    "deadlines",
    "submissions",
    "assessment",
    "grades",
    "reports",
    "official-submission",
  ],
  assessment: [
    "dashboard",
    "activeWorksheets",
    "assessments",
    "classActivities",
    "internalInternship",
    "btecProjects",
    "grading",
    "reports",
    "exports",
    "btecUpcoming",
    "btecGrading",
    "btecFinalDelivery",
    "btecReports",
    "btecExports",
  ],
  substitute: [
    "dashboard",
    "sessions",
    "absences",
    "live",
    "grading",
    "student",
    "reports",
    "exports",
  ],
  administration: [
    "dashboard",
    "crm",
    "phones",
    "minutes",
    "transcripts",
    "documents",
    "contracts",
    "suspensions",
    "btecStudents",
    "uniforms",
  ],
} as const;

export type NavigationScope = keyof typeof NAV_IDS;
export type NavigationRole = TestUserRole | "superadmin" | "administration" | "support_staff";
type NavigationIdByScope = {
  [Scope in NavigationScope]: (typeof NAV_IDS)[Scope][number];
};
export type NavigationId<Scope extends NavigationScope> = NavigationIdByScope[Scope];
type NavigationKey = {
  [Scope in NavigationScope]: `${Scope}.${NavigationIdByScope[Scope]}`;
}[NavigationScope];

const everyoneExceptFood: readonly NavigationRole[] = [
  "student",
  "parent",
  "professor",
  "student_adviser",
  "admin",
  "administration",
];
const assessmentStaff: readonly NavigationRole[] = ["student", "professor", "admin"];
const elearningUsers: readonly NavigationRole[] = ["student", "parent", "professor", "student_adviser", "admin", "administration"];
const btecStaff: readonly NavigationRole[] = ["professor", "admin"];
const adminOnly: readonly NavigationRole[] = ["admin"];
const administrationStaff: readonly NavigationRole[] = ["admin", "administration"];

export const NAV_VISIBILITY = {
  "dashboard.overview": [...everyoneExceptFood, "support_staff"],
  "dashboard.grades": ["student", "parent"],
  "dashboard.eLearning": elearningUsers,
  "dashboard.btec": everyoneExceptFood,
  "dashboard.assessment": assessmentStaff,
  "dashboard.parentAssessments": [],
  "dashboard.parentInternship": [],
  "dashboard.parentSubstitute": [],
  "dashboard.substitute": [...assessmentStaff, "administration"],
  "dashboard.administration": administrationStaff,
  "dashboard.studentCare": ["professor", "student_adviser", "parent"],
  "dashboard.chat": ["professor", "student_adviser", "student", "parent"],
  "dashboard.wellbeing": ["student"],
  "dashboard.messages": ["admin", "professor", "student_adviser", "student", "parent"],
  "dashboard.foodOrders": ["admin", "professor", "student_adviser", "student", "food_company", "administration"],
  "dashboard.foodPhotos": ["food_company", "superadmin"],
  "dashboard.foodStudents": ["food_company", "superadmin"],
  "dashboard.foodStaff": ["food_company", "superadmin"],
  "dashboard.feedback": [...everyoneExceptFood.filter((role) => role !== "parent"), "food_company", "support_staff"],
  "dashboard.help": [...everyoneExceptFood, "support_staff"],
  "dashboard.users": adminOnly,
  "dashboard.settings": ["admin", "professor"],

  "btec.dashboard": ["admin", "professor", "student", "parent"],
  "btec.studentStatus": ["student", "parent"],
  "btec.studentCalendar": ["student"],
  "btec.parentCalendar": ["parent"],
  "btec.parentFeedback": ["parent"],
  "btec.parentWorksheets": ["parent"],
  "btec.projects": btecStaff,
  "btec.personalTasks": ["professor"],
  "btec.myTasks": btecStaff,
  "btec.assessment": btecStaff,
  "btec.standardisation": btecStaff,
  "btec.reports": btecStaff,
  "btec.guides": btecStaff,
  "btec.notifications": btecStaff,
  "btec.analytics": btecStaff,
  "btec.scenarioRequests": btecStaff,
  "btec.settings": btecStaff,
  "btec.units": btecStaff,
  "btec.templates": adminOnly,

  "btecProject.overview": btecStaff,
  "btecProject.deadlines": btecStaff,
  "btecProject.submissions": btecStaff,
  "btecProject.assessment": btecStaff,
  "btecProject.grades": btecStaff,
  "btecProject.reports": btecStaff,
  "btecProject.official-submission": btecStaff,

  "assessment.dashboard": assessmentStaff,
  "assessment.activeWorksheets": ["admin", "professor"],
  "assessment.assessments": ["admin", "professor"],
  "assessment.classActivities": ["professor"],
  "assessment.internalInternship": assessmentStaff,
  "assessment.btecProjects": assessmentStaff,
  "assessment.grading": ["admin", "professor"],
  "assessment.reports": ["admin", "professor"],
  "assessment.exports": ["admin", "professor"],
  "assessment.btecUpcoming": ["admin", "professor"],
  "assessment.btecGrading": ["admin", "professor"],
  "assessment.btecFinalDelivery": ["admin", "professor"],
  "assessment.btecReports": ["admin", "professor"],
  "assessment.btecExports": ["admin", "professor"],

  "substitute.dashboard": assessmentStaff,
  "substitute.sessions": ["admin", "professor"],
  "substitute.absences": ["admin", "administration"],
  "substitute.reports": ["admin", "professor"],
  "substitute.exports": ["admin", "professor"],
  "substitute.live": ["admin", "professor"],
  "substitute.grading": ["admin", "professor"],
  "substitute.student": ["student"],

  "administration.dashboard": administrationStaff,
  "administration.crm": administrationStaff,
  "administration.phones": administrationStaff,
  "administration.minutes": administrationStaff,
  "administration.transcripts": administrationStaff,
  "administration.documents": administrationStaff,
  "administration.contracts": administrationStaff,
  "administration.suspensions": administrationStaff,
  "administration.btecStudents": administrationStaff,
  "administration.uniforms": administrationStaff,
} satisfies Record<NavigationKey, readonly NavigationRole[]>;

export function isNavigationVisible<Scope extends NavigationScope>(
  scope: Scope,
  id: NavigationIdByScope[Scope],
  role: NavigationRole,
): boolean {
  const allowedRoles = NAV_VISIBILITY[`${scope}.${id}` as NavigationKey] as readonly NavigationRole[];
  return allowedRoles.includes(role) || (role === "superadmin" && allowedRoles.includes("admin"));
}

export function filterNavigationIds(
  scope: NavigationScope,
  role: NavigationRole,
  candidates: readonly string[],
  featureVisibility?: Readonly<Record<string, boolean>>,
): string[] {
  return candidates.filter((id) => {
    if (featureVisibility?.[id] === false) return false;
    return isNavigationVisible(scope, id as NavigationIdByScope[typeof scope], role);
  });
}

export function visibleNavigationIds(
  scope: NavigationScope,
  role: NavigationRole,
  featureVisibility?: Readonly<Record<string, boolean>>,
): string[] {
  return filterNavigationIds(scope, role, NAV_IDS[scope], featureVisibility);
}
